import { useState } from 'react'
import { Globe, Loader2 } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuth } from '@/features/auth/hooks/use-auth'

const languageOptions = [
  { code: 'de', labelKey: 'german', flag: '🇩🇪' },
  { code: 'en', labelKey: 'english', flag: '🇬🇧' },
] as const

export function LanguageSwitcher() {
  const { i18n } = useTranslation()
  const { t: tCommon } = useTranslation('common')
  const { t: tToast } = useTranslation('toast')
  const { updateLocale, isAuthenticated } = useAuth()
  const [isUpdating, setIsUpdating] = useState(false)

  const languages = languageOptions.map((lang) => ({
    ...lang,
    label: tCommon(lang.labelKey),
  }))

  const currentLanguage =
    languages.find((lang) => lang.code === i18n.language) ?? languages[0]

  const handleLanguageChange = async (languageCode: string) => {
    if (languageCode === i18n.language || isUpdating) return

    setIsUpdating(true)
    try {
      if (isAuthenticated) {
        // Save to backend when authenticated
        await updateLocale(languageCode)
      } else {
        // Fallback to localStorage when not authenticated
        await i18n.changeLanguage(languageCode)
        localStorage.setItem('preferred-language', languageCode)
      }
      toast.success(tToast('languageChanged'))
    } catch {
      toast.error(tToast('languageChangeFailed'))
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          title={tCommon('language')}
          disabled={isUpdating}
        >
          {isUpdating ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Globe className="w-5 h-5" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={lang.code === currentLanguage.code ? 'bg-accent' : ''}
            disabled={isUpdating}
          >
            <span className="mr-2">{lang.flag}</span>
            {lang.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
